package kpt.core.database.banking.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kotlinx.datetime.LocalDate
import kpt.core.database.banking.converter.BankingTypeConverters
import kpt.core.database.banking.entity.LoanEntity
import kpt.core.model.banking.LoanKind

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class LoanDao_Impl(
  __db: RoomDatabase,
) : LoanDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfLoanEntity: EntityUpsertAdapter<LoanEntity>

  private val __bankingTypeConverters: BankingTypeConverters = BankingTypeConverters()
  init {
    this.__db = __db
    this.__upsertAdapterOfLoanEntity = EntityUpsertAdapter<LoanEntity>(object : EntityInsertAdapter<LoanEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `banking_loans` (`id`,`name`,`kind`,`principal`,`principalRemaining`,`annualRatePercent`,`tenureMonths`,`monthsRemaining`,`monthlyPayment`,`nextDueDate`,`totalPaid`,`createdAtMs`,`updatedAtMs`) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: LoanEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        val _tmp: String = __bankingTypeConverters.fromLoanKind(entity.kind)
        statement.bindText(3, _tmp)
        statement.bindDouble(4, entity.principal)
        statement.bindDouble(5, entity.principalRemaining)
        statement.bindDouble(6, entity.annualRatePercent)
        statement.bindLong(7, entity.tenureMonths.toLong())
        statement.bindLong(8, entity.monthsRemaining.toLong())
        statement.bindDouble(9, entity.monthlyPayment)
        val _tmp_1: String = __bankingTypeConverters.fromLocalDate(entity.nextDueDate)
        statement.bindText(10, _tmp_1)
        statement.bindDouble(11, entity.totalPaid)
        statement.bindLong(12, entity.createdAtMs)
        statement.bindLong(13, entity.updatedAtMs)
      }
    }, object : EntityDeleteOrUpdateAdapter<LoanEntity>() {
      protected override fun createQuery(): String = "UPDATE `banking_loans` SET `id` = ?,`name` = ?,`kind` = ?,`principal` = ?,`principalRemaining` = ?,`annualRatePercent` = ?,`tenureMonths` = ?,`monthsRemaining` = ?,`monthlyPayment` = ?,`nextDueDate` = ?,`totalPaid` = ?,`createdAtMs` = ?,`updatedAtMs` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: LoanEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        val _tmp: String = __bankingTypeConverters.fromLoanKind(entity.kind)
        statement.bindText(3, _tmp)
        statement.bindDouble(4, entity.principal)
        statement.bindDouble(5, entity.principalRemaining)
        statement.bindDouble(6, entity.annualRatePercent)
        statement.bindLong(7, entity.tenureMonths.toLong())
        statement.bindLong(8, entity.monthsRemaining.toLong())
        statement.bindDouble(9, entity.monthlyPayment)
        val _tmp_1: String = __bankingTypeConverters.fromLocalDate(entity.nextDueDate)
        statement.bindText(10, _tmp_1)
        statement.bindDouble(11, entity.totalPaid)
        statement.bindLong(12, entity.createdAtMs)
        statement.bindLong(13, entity.updatedAtMs)
        statement.bindText(14, entity.id)
      }
    })
  }

  public override suspend fun upsert(entity: LoanEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfLoanEntity.upsert(_connection, entity)
  }

  public override fun observeAll(): Flow<List<LoanEntity>> {
    val _sql: String = "SELECT * FROM banking_loans ORDER BY nextDueDate ASC, createdAtMs ASC"
    return createFlow(__db, false, arrayOf("banking_loans")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfKind: Int = getColumnIndexOrThrow(_stmt, "kind")
        val _columnIndexOfPrincipal: Int = getColumnIndexOrThrow(_stmt, "principal")
        val _columnIndexOfPrincipalRemaining: Int = getColumnIndexOrThrow(_stmt, "principalRemaining")
        val _columnIndexOfAnnualRatePercent: Int = getColumnIndexOrThrow(_stmt, "annualRatePercent")
        val _columnIndexOfTenureMonths: Int = getColumnIndexOrThrow(_stmt, "tenureMonths")
        val _columnIndexOfMonthsRemaining: Int = getColumnIndexOrThrow(_stmt, "monthsRemaining")
        val _columnIndexOfMonthlyPayment: Int = getColumnIndexOrThrow(_stmt, "monthlyPayment")
        val _columnIndexOfNextDueDate: Int = getColumnIndexOrThrow(_stmt, "nextDueDate")
        val _columnIndexOfTotalPaid: Int = getColumnIndexOrThrow(_stmt, "totalPaid")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: MutableList<LoanEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: LoanEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpKind: LoanKind
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfKind)
          _tmpKind = __bankingTypeConverters.toLoanKind(_tmp)
          val _tmpPrincipal: Double
          _tmpPrincipal = _stmt.getDouble(_columnIndexOfPrincipal)
          val _tmpPrincipalRemaining: Double
          _tmpPrincipalRemaining = _stmt.getDouble(_columnIndexOfPrincipalRemaining)
          val _tmpAnnualRatePercent: Double
          _tmpAnnualRatePercent = _stmt.getDouble(_columnIndexOfAnnualRatePercent)
          val _tmpTenureMonths: Int
          _tmpTenureMonths = _stmt.getLong(_columnIndexOfTenureMonths).toInt()
          val _tmpMonthsRemaining: Int
          _tmpMonthsRemaining = _stmt.getLong(_columnIndexOfMonthsRemaining).toInt()
          val _tmpMonthlyPayment: Double
          _tmpMonthlyPayment = _stmt.getDouble(_columnIndexOfMonthlyPayment)
          val _tmpNextDueDate: LocalDate
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfNextDueDate)
          _tmpNextDueDate = __bankingTypeConverters.toLocalDate(_tmp_1)
          val _tmpTotalPaid: Double
          _tmpTotalPaid = _stmt.getDouble(_columnIndexOfTotalPaid)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _item = LoanEntity(_tmpId,_tmpName,_tmpKind,_tmpPrincipal,_tmpPrincipalRemaining,_tmpAnnualRatePercent,_tmpTenureMonths,_tmpMonthsRemaining,_tmpMonthlyPayment,_tmpNextDueDate,_tmpTotalPaid,_tmpCreatedAtMs,_tmpUpdatedAtMs)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeById(id: String): Flow<LoanEntity?> {
    val _sql: String = "SELECT * FROM banking_loans WHERE id = ? LIMIT 1"
    return createFlow(__db, false, arrayOf("banking_loans")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfKind: Int = getColumnIndexOrThrow(_stmt, "kind")
        val _columnIndexOfPrincipal: Int = getColumnIndexOrThrow(_stmt, "principal")
        val _columnIndexOfPrincipalRemaining: Int = getColumnIndexOrThrow(_stmt, "principalRemaining")
        val _columnIndexOfAnnualRatePercent: Int = getColumnIndexOrThrow(_stmt, "annualRatePercent")
        val _columnIndexOfTenureMonths: Int = getColumnIndexOrThrow(_stmt, "tenureMonths")
        val _columnIndexOfMonthsRemaining: Int = getColumnIndexOrThrow(_stmt, "monthsRemaining")
        val _columnIndexOfMonthlyPayment: Int = getColumnIndexOrThrow(_stmt, "monthlyPayment")
        val _columnIndexOfNextDueDate: Int = getColumnIndexOrThrow(_stmt, "nextDueDate")
        val _columnIndexOfTotalPaid: Int = getColumnIndexOrThrow(_stmt, "totalPaid")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: LoanEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpKind: LoanKind
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfKind)
          _tmpKind = __bankingTypeConverters.toLoanKind(_tmp)
          val _tmpPrincipal: Double
          _tmpPrincipal = _stmt.getDouble(_columnIndexOfPrincipal)
          val _tmpPrincipalRemaining: Double
          _tmpPrincipalRemaining = _stmt.getDouble(_columnIndexOfPrincipalRemaining)
          val _tmpAnnualRatePercent: Double
          _tmpAnnualRatePercent = _stmt.getDouble(_columnIndexOfAnnualRatePercent)
          val _tmpTenureMonths: Int
          _tmpTenureMonths = _stmt.getLong(_columnIndexOfTenureMonths).toInt()
          val _tmpMonthsRemaining: Int
          _tmpMonthsRemaining = _stmt.getLong(_columnIndexOfMonthsRemaining).toInt()
          val _tmpMonthlyPayment: Double
          _tmpMonthlyPayment = _stmt.getDouble(_columnIndexOfMonthlyPayment)
          val _tmpNextDueDate: LocalDate
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfNextDueDate)
          _tmpNextDueDate = __bankingTypeConverters.toLocalDate(_tmp_1)
          val _tmpTotalPaid: Double
          _tmpTotalPaid = _stmt.getDouble(_columnIndexOfTotalPaid)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _result = LoanEntity(_tmpId,_tmpName,_tmpKind,_tmpPrincipal,_tmpPrincipalRemaining,_tmpAnnualRatePercent,_tmpTenureMonths,_tmpMonthsRemaining,_tmpMonthlyPayment,_tmpNextDueDate,_tmpTotalPaid,_tmpCreatedAtMs,_tmpUpdatedAtMs)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getById(id: String): LoanEntity? {
    val _sql: String = "SELECT * FROM banking_loans WHERE id = ? LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfKind: Int = getColumnIndexOrThrow(_stmt, "kind")
        val _columnIndexOfPrincipal: Int = getColumnIndexOrThrow(_stmt, "principal")
        val _columnIndexOfPrincipalRemaining: Int = getColumnIndexOrThrow(_stmt, "principalRemaining")
        val _columnIndexOfAnnualRatePercent: Int = getColumnIndexOrThrow(_stmt, "annualRatePercent")
        val _columnIndexOfTenureMonths: Int = getColumnIndexOrThrow(_stmt, "tenureMonths")
        val _columnIndexOfMonthsRemaining: Int = getColumnIndexOrThrow(_stmt, "monthsRemaining")
        val _columnIndexOfMonthlyPayment: Int = getColumnIndexOrThrow(_stmt, "monthlyPayment")
        val _columnIndexOfNextDueDate: Int = getColumnIndexOrThrow(_stmt, "nextDueDate")
        val _columnIndexOfTotalPaid: Int = getColumnIndexOrThrow(_stmt, "totalPaid")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: LoanEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpKind: LoanKind
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfKind)
          _tmpKind = __bankingTypeConverters.toLoanKind(_tmp)
          val _tmpPrincipal: Double
          _tmpPrincipal = _stmt.getDouble(_columnIndexOfPrincipal)
          val _tmpPrincipalRemaining: Double
          _tmpPrincipalRemaining = _stmt.getDouble(_columnIndexOfPrincipalRemaining)
          val _tmpAnnualRatePercent: Double
          _tmpAnnualRatePercent = _stmt.getDouble(_columnIndexOfAnnualRatePercent)
          val _tmpTenureMonths: Int
          _tmpTenureMonths = _stmt.getLong(_columnIndexOfTenureMonths).toInt()
          val _tmpMonthsRemaining: Int
          _tmpMonthsRemaining = _stmt.getLong(_columnIndexOfMonthsRemaining).toInt()
          val _tmpMonthlyPayment: Double
          _tmpMonthlyPayment = _stmt.getDouble(_columnIndexOfMonthlyPayment)
          val _tmpNextDueDate: LocalDate
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfNextDueDate)
          _tmpNextDueDate = __bankingTypeConverters.toLocalDate(_tmp_1)
          val _tmpTotalPaid: Double
          _tmpTotalPaid = _stmt.getDouble(_columnIndexOfTotalPaid)
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _result = LoanEntity(_tmpId,_tmpName,_tmpKind,_tmpPrincipal,_tmpPrincipalRemaining,_tmpAnnualRatePercent,_tmpTenureMonths,_tmpMonthsRemaining,_tmpMonthlyPayment,_tmpNextDueDate,_tmpTotalPaid,_tmpCreatedAtMs,_tmpUpdatedAtMs)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun count(): Flow<Int> {
    val _sql: String = "SELECT COUNT(*) FROM banking_loans"
    return createFlow(__db, false, arrayOf("banking_loans")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteById(id: String) {
    val _sql: String = "DELETE FROM banking_loans WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM banking_loans"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
